var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import React, { useState, useEffect, useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { observer } from 'mobx-react';
import { Button, Spin, Typography, Form, message, Divider, Switch } from 'antd';
import { RiTimerLine, RiCheckLine } from 'react-icons/ri';
import { Permissions, Automations } from '@whatsper/texterchat-common';
import ModalWithoutBottom from '../../../../Chat/common/ModalWithoutBottom';
import { useHistory } from 'react-router-dom';
import useCheckIfMobile from '../../../../../helpers/hooks/useCheckIfMobile';
import { useStore } from '../../../../../store/rootStore';
import StatusSection from './components/StatusSection';
import IdleThresholdSection from './components/IdleThresholdSection';
import { STATUS_LABEL_KEYS, DEFAULT_SETTINGS, DEFAULT_IDLE_THRESHOLD_HOURS, MAX_HOURS, } from './constants';
import { parseHoursToState, stateToHours } from './helpers';
import s from './AutoResolveChats.module.scss';
const { Text } = Typography;
const AutoResolveChats = () => {
    const { t } = useTranslation();
    const isMobile = useCheckIfMobile();
    const history = useHistory();
    const { agentsStore, settings, checkFeatureAvailability } = useStore();
    const [submitting, setSubmitting] = useState(false);
    const [statusStates, setStatusStates] = useState({});
    const [globalEnabled, setGlobalEnabled] = useState(true);
    const [idleThresholdState, setIdleThresholdState] = useState(parseHoursToState({ enabled: true, hours: DEFAULT_IDLE_THRESHOLD_HOURS }));
    const featureEnabled = checkFeatureAvailability('autoResolveChatsAutomation');
    const canManageAutoResolve = agentsStore.hasPermissions([Permissions.Permission.automations_manage]) && !!featureEnabled;
    useEffect(() => {
        if (settings.generalSettingsLoading)
            return;
        if (!featureEnabled) {
            history.push('/');
        }
    }, [featureEnabled, history, settings.generalSettingsLoading]);
    const timeOptions = useMemo(() => [
        { value: '12', label: t('timeOption12Hours') },
        { value: '24', label: t('timeOption24Hours') },
        { value: '36', label: t('timeOption36Hours') },
        { value: '48', label: t('timeOption48Hours') },
        { value: '72', label: t('timeOption72Hours') },
        { value: 'custom', label: t('timeOptionCustom') },
    ], [t]);
    const unitOptions = useMemo(() => [
        { value: 'hours', label: t('unitHours') },
        { value: 'days', label: t('unitDays') },
    ], [t]);
    const getValidationError = (state) => {
        if (!state.enabled)
            return null;
        const hours = stateToHours(state);
        if (hours > MAX_HOURS) {
            return t('autoResolveMaxHoursError', { max: MAX_HOURS });
        }
        return null;
    };
    const getHoursValidationError = (state) => {
        const hours = stateToHours(state);
        if (hours > MAX_HOURS) {
            return t('autoResolveMaxHoursError', { max: MAX_HOURS });
        }
        return null;
    };
    const validationErrors = {};
    for (const status of Automations.Chats.AutoResolve.AUTO_RESOLVE_STATUSES) {
        const key = String(status);
        const state = statusStates[key];
        validationErrors[key] = state ? getValidationError(state) : null;
    }
    const idleThresholdError = getHoursValidationError(idleThresholdState);
    const hasValidationErrors = Object.values(validationErrors).some((e) => e !== null) || idleThresholdError !== null;
    useEffect(() => {
        if (!featureEnabled)
            return;
        settings.getAutoResolveSettings().then((data) => {
            var _a;
            if (data) {
                setGlobalEnabled(data.enabled);
                setIdleThresholdState(parseHoursToState({ enabled: true, hours: (_a = data.idleThresholdHours) !== null && _a !== void 0 ? _a : DEFAULT_IDLE_THRESHOLD_HOURS }));
                const states = {};
                for (const status of Automations.Chats.AutoResolve.AUTO_RESOLVE_STATUSES) {
                    const key = String(status);
                    states[key] = parseHoursToState(data[status] || DEFAULT_SETTINGS);
                }
                setStatusStates(states);
            }
        });
    }, []);
    const onBack = () => {
        history.push('/');
    };
    const updateStatus = (key, newState) => {
        setStatusStates((prev) => (Object.assign(Object.assign({}, prev), { [key]: newState })));
    };
    const onSubmit = () => __awaiter(void 0, void 0, void 0, function* () {
        if (hasValidationErrors) {
            message.error(React.createElement(Trans, { i18nKey: "fixValidationErrorsBeforeSaving" }));
            return;
        }
        try {
            setSubmitting(true);
            const perStatus = {};
            for (const status of Automations.Chats.AutoResolve.AUTO_RESOLVE_STATUSES) {
                const state = statusStates[String(status)];
                if (!state)
                    continue;
                perStatus[status] = {
                    enabled: state.enabled,
                    hours: stateToHours(state),
                };
            }
            const payload = Object.assign({ enabled: globalEnabled, idleThresholdHours: stateToHours(idleThresholdState) }, perStatus);
            yield settings.updateAutoResolveSettings(payload);
            message.success({
                content: React.createElement(Trans, { i18nKey: "autoResolveSettingsSaved" }),
                icon: React.createElement(RiCheckLine, { className: s.successIcon }),
                className: s.successMessage,
            });
            onBack();
        }
        catch (error) {
            console.error('Failed to submit auto resolve chats settings', error);
            message.error(React.createElement(Trans, { i18nKey: "autoResolveSettingsSaveError" }));
        }
        finally {
            setSubmitting(false);
        }
    });
    if (settings.autoResolveLoading) {
        return (React.createElement(ModalWithoutBottom, { setModalVisible: onBack, titleTextKey: "autoResolveChats", width: isMobile ? undefined : 600, visible: true, headerIcon: () => React.createElement(RiTimerLine, { className: s.titleIcon }) },
            React.createElement("div", { className: s.content },
                React.createElement("div", { className: s.loadingContainer },
                    React.createElement(Spin, { size: "large" })))));
    }
    return (React.createElement(ModalWithoutBottom, { setModalVisible: onBack, titleTextKey: "autoResolveChats", width: isMobile ? undefined : 600, visible: true, headerIcon: () => React.createElement(RiTimerLine, { className: s.titleIcon }) },
        React.createElement("div", { className: s.content },
            React.createElement("div", { className: s.formContainer },
                React.createElement(Form, { layout: "vertical", className: s.form },
                    React.createElement(Form.Item, null,
                        React.createElement("div", { className: s.switchContainer },
                            React.createElement(Text, null,
                                React.createElement(Trans, { i18nKey: "autoResolveEnabled" })),
                            React.createElement(Switch, { checked: globalEnabled, onChange: setGlobalEnabled, disabled: !canManageAutoResolve }))),
                    React.createElement(Divider, { className: s.sectionDivider }),
                    React.createElement("div", { className: !globalEnabled ? s.statusGroupDisabled : undefined }, Automations.Chats.AutoResolve.AUTO_RESOLVE_STATUSES.map((status, index) => {
                        const key = String(status);
                        const labelKey = STATUS_LABEL_KEYS[status];
                        const defaultState = {
                            enabled: false,
                            duration: '48',
                            customValue: 1,
                            customUnit: 'days',
                        };
                        return (React.createElement(React.Fragment, { key: key },
                            index > 0 && React.createElement(Divider, { className: s.sectionDivider }),
                            React.createElement(StatusSection, { statusLabel: labelKey, state: statusStates[key] || defaultState, onChange: (newState) => updateStatus(key, newState), timeOptions: timeOptions, unitOptions: unitOptions, error: validationErrors[key] })));
                    })),
                    React.createElement(Divider, { className: s.sectionDivider }),
                    React.createElement(IdleThresholdSection, { state: idleThresholdState, onChange: setIdleThresholdState, timeOptions: timeOptions, unitOptions: unitOptions, error: idleThresholdError }))),
            React.createElement("div", { className: s.footer },
                React.createElement(Button, { onClick: onBack, className: s.actionButton, disabled: submitting },
                    React.createElement(Trans, { i18nKey: "cancel" })),
                React.createElement(Button, { type: "primary", onClick: onSubmit, className: s.actionButton, loading: submitting, disabled: submitting || !canManageAutoResolve || hasValidationErrors },
                    React.createElement(Trans, { i18nKey: "set" }))))));
};
export default observer(AutoResolveChats);
